/**
 * TableContextToolbar.tsx
 * Word-like table tools for the template canvas: insert/delete rows and
 * columns, cell shading, borders, add/remove a whole table.
 *
 * The canvas is re-rendered from the DOCX after every save, which detaches the
 * elements this toolbar points at. Selection is therefore always re-validated
 * against the live DOM before an operation runs — a stale reference used to
 * make every button look broken.
 */
import * as React from "react";
import {
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Trash2,
  Plus,
  Grid3X3,
  Paintbrush,
  TableProperties,
  MousePointerClick,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  findCellPosition,
  insertRowAbove,
  insertRowBelow,
  deleteRow,
  insertColumnLeft,
  insertColumnAt,
  deleteColumn,
  deleteTable,
  toggleTableBorders,
  setCellBackground,
  getTableDimensions,
  insertDataTableAtCursor,
} from "@/utils/tableOps";
import type { CellPosition } from "@/utils/tableOps";

interface Props {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onChanged?: () => void;
}

const PRESET_COLORS = [
  "#ffffff", "#f1f5f9", "#e2e8f0",
  "#fef3c7", "#fde68a",
  "#d1fae5", "#a7f3d0",
  "#dbeafe", "#bfdbfe",
  "#fce7f3", "#fbcfe8",
  "#fee2e2", "#fecaca",
];

/** Only the column count is asked for: printed rows come from the bound data. */
const COLUMN_CHOICES = [2, 3, 4, 5, 6, 7, 8];

const HIGHLIGHT_ATTR = "data-table-cell-active";

/** Outline the active cell so the user can see what the buttons will act on. */
function highlightCell(container: HTMLElement | null, cell: HTMLTableCellElement | null): void {
  container
    ?.querySelectorAll<HTMLElement>(`[${HIGHLIGHT_ATTR}]`)
    .forEach((el) => {
      el.removeAttribute(HIGHLIGHT_ATTR);
      el.style.outline = "";
      el.style.outlineOffset = "";
    });
  if (cell) {
    cell.setAttribute(HIGHLIGHT_ATTR, "true");
    cell.style.outline = "2px solid #6366f1";
    cell.style.outlineOffset = "-2px";
  }
}

function InsertTableMenu({ label, onPick }: { label: string; onPick: (cols: number) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 px-1.5 gap-1" title="Chèn một bảng dữ liệu mới">
          <Plus className="h-3.5 w-3.5" />
          <span className="text-[10px]">Bảng dữ liệu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-xs">{label}</DropdownMenuLabel>
        <div className="px-2 pb-1 text-[10px] text-muted-foreground">
          Số hàng in ra do dữ liệu quyết định, chỉ cần chọn số cột.
        </div>
        <DropdownMenuSeparator />
        {COLUMN_CHOICES.map((cols) => (
          <DropdownMenuItem key={cols} className="text-xs" onClick={() => onPick(cols)}>
            <Grid3X3 className="h-3.5 w-3.5 mr-2" />
            {cols} cột
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TableContextToolbar({ containerRef, onChanged }: Props) {
  const [cellPos, setCellPos] = React.useState<CellPosition | null>(null);
  const [dims, setDims] = React.useState({ rows: 0, cols: 0 });
  const [tableCount, setTableCount] = React.useState(0);

  const select = React.useCallback(
    (cell: HTMLTableCellElement | null) => {
      const container = containerRef.current;
      const pos = cell && cell.isConnected ? findCellPosition(cell) : null;
      highlightCell(container, pos?.cell ?? null);
      setCellPos(pos);
      setDims(pos ? getTableDimensions(pos.table) : { rows: 0, cols: 0 });
    },
    [containerRef]
  );

  // Track selection and keep it honest when the canvas re-renders.
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handlePointer = (e: Event) => {
      const target = e.target as HTMLElement | null;
      const cell = target?.closest("td, th") as HTMLTableCellElement | null;
      if (cell && container.contains(cell)) select(cell);
    };

    const observer = new MutationObserver(() => {
      setTableCount(container.querySelectorAll("table").length);
      setCellPos((prev) => (prev && prev.cell.isConnected ? prev : null));
    });

    container.addEventListener("pointerdown", handlePointer);
    container.addEventListener("focusin", handlePointer);
    observer.observe(container, { childList: true, subtree: true });
    setTableCount(container.querySelectorAll("table").length);

    return () => {
      container.removeEventListener("pointerdown", handlePointer);
      container.removeEventListener("focusin", handlePointer);
      observer.disconnect();
      highlightCell(container, null);
    };
  }, [containerRef, select]);

  /** Run a table operation against a freshly resolved position. */
  const exec = (fn: (pos: CellPosition) => HTMLTableCellElement | null | void) => {
    if (!cellPos || !cellPos.cell.isConnected) {
      select(null);
      return;
    }
    const pos = findCellPosition(cellPos.cell);
    if (!pos) {
      select(null);
      return;
    }
    const next = fn(pos);
    select(next === undefined ? pos.cell : next);
    onChanged?.();
  };

  const insertTable = (cols: number, anchor?: HTMLTableCellElement | null) => {
    const container = containerRef.current;
    if (!container) return;
    const table = insertDataTableAtCursor(container, cols, anchor ?? null);
    select(table.querySelector("td"));
    onChanged?.();
  };

  if (!cellPos) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-dashed bg-muted/30 px-2 py-1 text-[11px] text-muted-foreground">
        <MousePointerClick className="h-3.5 w-3.5 text-primary" />
        {tableCount > 0
          ? "Bấm vào một ô trong bảng để hiện công cụ thêm/xóa hàng, cột."
          : "Template chưa có bảng nào."}
        <InsertTableMenu label="Chèn bảng dữ liệu vào cuối trang" onPick={(c) => insertTable(c)} />
      </div>
    );
  }

  const { rowIndex } = cellPos;

  return (
    <div className="flex flex-wrap items-center gap-1 rounded-lg border border-primary/30 bg-card px-2 py-1 shadow-sm text-xs animate-in fade-in-0 slide-in-from-top-1 duration-200">
      <div className="flex items-center gap-1 pr-1.5 border-r">
        <TableProperties className="h-3.5 w-3.5 text-primary" />
        <Badge variant="outline" className="text-[10px] font-mono h-5 px-1.5 bg-primary/5">
          Ô H{rowIndex + 1}·C{cellPos.gridIndex + 1}
        </Badge>
        <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
          bảng {dims.rows}×{dims.cols}
        </Badge>
      </div>

      {/* Rows */}
      <div className="flex items-center gap-0.5">
        <span className="text-[10px] text-muted-foreground px-1">Hàng</span>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-1.5 gap-0.5"
          title="Thêm một hàng trống phía trên hàng đang chọn"
          onClick={() => exec((p) => void insertRowAbove(p.table, p.rowIndex))}
        >
          <Plus className="h-3 w-3" />
          <ArrowUp className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-1.5 gap-0.5"
          title="Thêm một hàng trống phía dưới hàng đang chọn"
          onClick={() => exec((p) => void insertRowBelow(p.table, p.rowIndex))}
        >
          <Plus className="h-3 w-3" />
          <ArrowDown className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-1.5 gap-0.5 text-red-500 hover:text-red-700 hover:bg-red-50"
          title="Xóa hàng đang chọn"
          disabled={dims.rows <= 1}
          onClick={() =>
            exec((p) => {
              if (!deleteRow(p.table, p.rowIndex)) return null;
              const rows = p.table.querySelectorAll("tr");
              const row = rows[Math.min(p.rowIndex, rows.length - 1)];
              return row?.cells[Math.min(p.colIndex, row.cells.length - 1)] ?? null;
            })
          }
        >
          <Trash2 className="h-3 w-3" />
          <span className="text-[10px]">Hàng</span>
        </Button>
      </div>

      <Separator orientation="vertical" className="mx-0.5 h-5" />

      {/* Columns */}
      <div className="flex items-center gap-0.5">
        <span className="text-[10px] text-muted-foreground px-1">Cột</span>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-1.5 gap-0.5"
          title="Thêm một cột trống bên trái cột đang chọn"
          onClick={() => exec((p) => void insertColumnLeft(p.table, p.gridIndex))}
        >
          <Plus className="h-3 w-3" />
          <ArrowLeft className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-1.5 gap-0.5"
          title="Thêm một cột trống bên phải cột đang chọn"
          onClick={() => exec((p) => void insertColumnAt(p.table, p.gridIndex + p.gridSpan))}
        >
          <Plus className="h-3 w-3" />
          <ArrowRight className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-1.5 gap-0.5 text-red-500 hover:text-red-700 hover:bg-red-50"
          title="Xóa cột đang chọn trên mọi hàng"
          disabled={dims.cols <= 1}
          onClick={() =>
            exec((p) => {
              if (!deleteColumn(p.table, p.gridIndex)) return null;
              const row = p.table.querySelectorAll("tr")[p.rowIndex];
              return row?.cells[Math.min(p.colIndex, row.cells.length - 1)] ?? null;
            })
          }
        >
          <Trash2 className="h-3 w-3" />
          <span className="text-[10px]">Cột</span>
        </Button>
      </div>

      <Separator orientation="vertical" className="mx-0.5 h-5" />

      {/* Cell shading + borders */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" className="h-7 px-1.5 gap-1" title="Màu nền của ô đang chọn">
            <Paintbrush className="h-3.5 w-3.5" />
            <span className="text-[10px]">Màu nền ô</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-auto p-2">
          <DropdownMenuLabel className="text-[11px] p-0 pb-1.5">Màu nền ô</DropdownMenuLabel>
          <div className="grid grid-cols-7 gap-1">
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                className="w-6 h-6 rounded border border-muted-foreground/20 hover:ring-2 hover:ring-primary/50 transition-all"
                style={{ backgroundColor: color }}
                title={color}
                onClick={() => exec((p) => void setCellBackground(p.cell, color))}
              />
            ))}
          </div>
          <div className="mt-2 pt-1.5 border-t">
            <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span>Tùy chọn:</span>
              <input
                type="color"
                className="h-5 w-6 cursor-pointer rounded border p-0 bg-transparent"
                onChange={(e) => {
                  const color = e.target.value;
                  exec((p) => void setCellBackground(p.cell, color));
                }}
              />
            </label>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-1.5 gap-1"
        title="Bật/tắt đường viền của bảng này"
        onClick={() => exec((p) => void toggleTableBorders(p.table))}
      >
        <Grid3X3 className="h-3.5 w-3.5" />
        <span className="text-[10px]">Đường viền</span>
      </Button>

      <Separator orientation="vertical" className="mx-0.5 h-5" />

      {/* Whole-table actions */}
      <InsertTableMenu
        label="Chèn bảng dữ liệu dưới bảng này"
        onPick={(c) => insertTable(c, cellPos.cell)}
      />

      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-1.5 gap-1 text-red-500 hover:text-red-700 hover:bg-red-50"
        title="Xóa toàn bộ bảng đang chọn"
        onClick={() =>
          exec((p) => {
            const { rows, cols } = getTableDimensions(p.table);
            const ok = window.confirm(`Xóa toàn bộ bảng ${rows}×${cols} này khỏi template?`);
            if (!ok) return p.cell;
            deleteTable(p.table);
            return null;
          })
        }
      >
        <Trash2 className="h-3.5 w-3.5" />
        <span className="text-[10px]">Xóa bảng</span>
      </Button>

      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-1 ml-auto"
        title="Bỏ chọn ô"
        onClick={() => select(null)}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}
